import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Expand, ShieldCheck } from "lucide-react";
import { siteConfig, imgSrc, imgSrcSet } from "@/config/siteConfig";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";

export const Jobs = () => {
  const [active, setActive] = useState(null);
  const items = siteConfig.gallery;

  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (dir) => setActive((a) => (a === null ? null : (a + dir + items.length) % items.length)),
    [items.length],
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, step]);

  return (
    <section id="isler" data-testid="jobs-section" className="scroll-mt-20 bg-ink-950 py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-5">
        <SectionHeading
          eyebrow="Yaptığımız İşlerden"
          title="Gerçek İşlerimiz"
          dark
          testid="jobs-heading"
        />
        <Reveal delay={0.05}>
          <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-white/10 bg-ink-800/60 p-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex max-w-xl items-start gap-3 text-sm leading-relaxed text-white/70" data-testid="gallery-trust-note">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              Fotoğrafların tamamı kendi aracımızla gerçekleştirdiğimiz gerçek taşıma işlerimizden alınmıştır.
            </p>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/45" data-testid="gallery-scope">
              {siteConfig.galleryScope.join(" • ")}
            </p>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          {items.map((item, i) => (
            <Reveal key={item.image} delay={(i % 3) * 0.06}>
              <button
                type="button"
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(i)}
                aria-label={`${item.title} fotoğrafını büyüt`}
                className="group relative block w-full overflow-hidden rounded-2xl border border-white/10"
              >
                <img
                  src={imgSrc(item.image, 800)}
                  srcSet={imgSrcSet(item.image)}
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 33vw"
                  alt={item.alt}
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: item.position }}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-ink-950/85 via-transparent to-transparent" aria-hidden="true" />
                <span className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-ink-950/60 text-white opacity-0 backdrop-blur transition-opacity duration-200 group-hover:opacity-100" aria-hidden="true">
                  <Expand className="h-4 w-4" />
                </span>
                <span className="absolute inset-x-0 bottom-0 p-3 text-left sm:p-4">
                  <span className="block font-display text-xs font-extrabold uppercase tracking-wide text-white sm:text-sm">
                    {item.title}
                  </span>
                  <span className="mt-0.5 block text-[11px] font-semibold text-white/60 sm:text-xs">{item.tag}</span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {active !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={items[active].title}
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-950/95 p-4 backdrop-blur-sm"
          onClick={close}
        >
          <button
            type="button"
            data-testid="lightbox-close"
            aria-label="Kapat"
            onClick={close}
            className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-brand"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            data-testid="lightbox-prev"
            aria-label="Önceki fotoğraf"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-brand md:left-6"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <figure className="max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={imgSrc(items[active].image, 1200)}
              alt={items[active].alt}
              className="max-h-[80vh] w-auto max-w-full rounded-xl object-contain"
            />
            <figcaption className="mt-4 text-center font-display text-sm font-bold uppercase tracking-wide text-white/80">
              {items[active].title} — {items[active].tag}
            </figcaption>
          </figure>
          <button
            type="button"
            data-testid="lightbox-next"
            aria-label="Sonraki fotoğraf"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-brand md:right-6"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      )}
    </section>
  );
};
